/**
 * Nucery cart drawer — /cart.js + /cart/change.js, syncs with Impulse cart:updated.
 */
(function () {
  'use strict';

  /**
   * Locale/market-aware cart URL. Shopify Markets prefixes storefront routes
   * (e.g. /en-sa/cart.js), so a bare /cart.js 404s on localized URLs. Use the
   * route root Shopify exposes — it is '/' on the primary locale.
   */
  function cartUrl(path) {
    var base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return base + path;
  }

  var TRASH_ICON =
    '<span class="nucery-cart-drawer__item-remove-ic" aria-hidden="true">' +
    '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.65" stroke-linecap="round" stroke-linejoin="round">' +
    '<path d="M3 6h18"></path><path d="M8 6V4a1 1 0 011-1h6a1 1 0 011 1v2"></path>' +
    '<path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6"></path><path d="M10 11v6M14 11v6"></path></svg></span>';

  var root;
  var config = {};
  var open = false;
  var prevTierState = null;
  function removeIconInner() {
    if (config.removeIconStyle === 'x') {
      return '<span class="nucery-cart-drawer__item-remove-x" aria-hidden="true">×</span>';
    }
    return TRASH_ICON;
  }

  function qs(sel, ctx) {
    return (ctx || root).querySelector(sel);
  }

  function qsa(sel, ctx) {
    return Array.prototype.slice.call((ctx || root).querySelectorAll(sel));
  }

  function ensureRoot() {
    root = document.getElementById('NuceryCartDrawer');
    return root;
  }

  /** Re-parse section JSON (theme editor swaps section HTML; config must stay with this drawer). */
  function readConfig() {
    var drawer = ensureRoot();
    var cfgEl = drawer && drawer.querySelector('script[type="application/json"][data-nucery-cart-config]');
    if (!cfgEl) cfgEl = document.getElementById('NuceryCartDrawerConfig');
    try {
      config = cfgEl ? JSON.parse(cfgEl.textContent) : {};
    } catch (err) {
      config = {};
    }
    if (config.trackStartOverride === '') config.trackStartOverride = null;
    config.strings = config.strings || {};
    config.shopCurrency = config.shopCurrency || config.currency || 'USD';
    applyThresholdsFromConfig();
    if (root) {
      var dsProgress = root.getAttribute('data-show-progress');
      if (dsProgress === 'false') config.showProgress = false;
      else if (dsProgress === 'true') config.showProgress = true;
    }
    if (config.showProgress !== false && config.showProgress !== 'false') {
      config.showProgress = true;
    } else {
      config.showProgress = false;
    }
  }

  function isProgressEnabled() {
    if (config.showProgress === false || config.showProgress === 'false') return false;
    if (root && root.getAttribute('data-show-progress') === 'false') return false;
    return config.showProgress !== false;
  }

  function clearProgressState() {
    if (!root) return;
    root.classList.remove('ncd--ship-reached', 'ncd--gift-reached', 'ncd--just-ship', 'ncd--just-gift');
    prevTierState = null;
  }

  /** Cart total in smallest presentment unit (same as /cart.js line prices). */
  function cartTotalCents(cart) {
    if (!cart) return 0;
    // Trust Shopify's post-discount total — INCLUDING 0 (e.g. a 100%-off code).
    // Only fall back to line/subtotal sums when total_price is genuinely missing
    // (NaN), never just because it's zero.
    var tp = Number(cart.total_price);
    if (isFinite(tp)) return tp;
    if (cart.items && cart.items.length) {
      var sum = 0;
      for (var i = 0; i < cart.items.length; i++) {
        sum += Number(cart.items[i].final_line_price) || 0;
      }
      if (sum > 0) return sum;
    }
    var sub = Number(cart.items_subtotal_price);
    if (isFinite(sub) && sub > 0) return sub;
    var ot = Number(cart.original_total_price);
    if (isFinite(ot) && ot > 0) return ot;
    return 0;
  }

  // Active display currency + base→active rate (storefront multi-currency).
  function activeCurrency() {
    return (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || config.currency || config.shopCurrency || 'USD';
  }
  function activeRate() {
    var r = (window.Shopify && window.Shopify.currency) ? parseFloat(window.Shopify.currency.rate) : NaN;
    return (isFinite(r) && r > 0) ? r : 1;
  }
  function formatMoneyRaw(cents) {
    cents = Number(cents) || 0;
    var shopCur = (config.shopCurrency || config.currency || 'USD').toString();
    var active = String(activeCurrency());
    var fmt = config.moneyFormat || (window.theme && window.theme.settings && window.theme.settings.moneyFormat);
    var shopifyOk = window.Shopify && typeof Shopify.formatMoney === 'function' && fmt;
    // Viewing the base currency → use the theme money format as-is.
    if (active.toUpperCase() === shopCur.toUpperCase()) {
      if (shopifyOk) return Shopify.formatMoney(cents, fmt);
      try {
        return new Intl.NumberFormat(undefined, { style: 'currency', currency: active }).format(cents / 100);
      } catch (e) {
        return (cents / 100).toFixed(2) + ' ' + active;
      }
    }
    // Different display currency → convert base→active by the storefront rate.
    var converted = cents * activeRate();
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: active }).format(converted / 100);
    } catch (e) {
      return (converted / 100).toFixed(2) + ' ' + active;
    }
  }

  function stripHtml(html) {
    var d = document.createElement('div');
    d.innerHTML = String(html == null ? '' : html);
    return (d.textContent || d.innerText || '').trim();
  }

  /**
   * Every drawer price is emitted as PLAIN TEXT (no `.money` span) so the Vitals
   * currency converter can't attach a base-currency hover tooltip to any price
   * in the cart drawer. Amounts are already in the active presentment currency,
   * and the drawer's color rules also target the parent price nodes — so dropping
   * the `.money` wrapper changes nothing visually except removing the tooltip.
   */
  function formatMoney(cents) {
    return stripHtml(formatMoneyRaw(cents));
  }

  // Retained for callers that explicitly request plain text (now identical).
  function formatMoneyPlain(cents) {
    return formatMoney(cents);
  }

  function setBodyScroll(lock) {
    document.documentElement.classList.toggle('nucery-cart-drawer--open', lock);
    document.body.classList.toggle('nucery-cart-drawer--open', lock);
  }

  function updateHeaderBadges(count) {
    Array.prototype.forEach.call(document.querySelectorAll('[data-nucery-cart-count]'), function (el) {
      el.textContent = String(count);
      el.hidden = count < 1;
      el.setAttribute('aria-hidden', count < 1 ? 'true' : 'false');
    });
  }

  function fetchCart() {
    return fetch(cartUrl('cart.js'), { credentials: 'same-origin', headers: { Accept: 'application/json' } }).then(function (r) {
      return r.json();
    });
  }

  function findLineItemRow(key) {
    if (!root || !key) return null;
    return root.querySelector('.nucery-cart-drawer__item[data-line-key="' + CSS.escape(String(key)) + '"]');
  }

  function changeLine(key, quantity) {
    if (!ensureRoot()) return Promise.resolve();
    var row = findLineItemRow(key);
    if (row) row.classList.add('nucery-cart-drawer__item--busy');
    return fetch(cartUrl('cart/change.js'), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity }),
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        if (cart && Array.isArray(cart.items)) applyCart(cart, { animate: false });
        return cart;
      })
      .finally(function () {
        if (row) row.classList.remove('nucery-cart-drawer__item--busy');
      });
  }

  /**
   * Debounce the /cart/change.js call per line so rapid +/- clicks collapse into
   * a single network request to the final quantity (price/qty already update
   * optimistically in the click handler, so it feels instant).
   */
  var lineChangeTimers = {};
  var LINE_DEBOUNCE_MS = 300;
  function queueLineChange(key, quantity) {
    if (lineChangeTimers[key]) clearTimeout(lineChangeTimers[key]);
    lineChangeTimers[key] = setTimeout(function () {
      delete lineChangeTimers[key];
      changeLine(key, quantity).catch(function () {});
    }, LINE_DEBOUNCE_MS);
  }

  // Remove a whole bundle group at once: every line in the group (anchor +
  // pieces) is set to 0 in a single /cart/update.js call. Keys are read from the
  // group container's data-bundle-keys.
  function removeBundle(name) {
    if (!ensureRoot()) return Promise.resolve();
    var updates = {};
    qsa('[data-bundle-keys]').forEach(function (g) {
      if (g.getAttribute('data-bundle') !== name) return;
      g.classList.add('nucery-cart-drawer__item--busy');
      (g.getAttribute('data-bundle-keys') || '').split(',').forEach(function (k) { if (k) updates[k] = 0; });
    });
    if (!Object.keys(updates).length) return Promise.resolve();
    return fetch(cartUrl('cart/update.js'), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates: updates }),
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (cart && Array.isArray(cart.items)) applyCart(cart, { animate: false });
        return cart;
      });
  }

  function lineItemImageUrl(item) {
    var imgUrl = item.image || (item.featured_image && item.featured_image.url) || '';
    if (imgUrl && imgUrl.indexOf('width=') === -1) {
      imgUrl += (imgUrl.indexOf('?') === -1 ? '?' : '&') + 'width=200';
    }
    return imgUrl;
  }

  // Build a readable variant label for a line item, e.g. "Gold / 18\"".
  // Prefers options_with_values (skips "Title"/"Default Title") and falls back
  // to variant_title. Returns an HTML-escaped string ('' when no real variant).
  function lineItemVariantLabel(item) {
    if (!item) return '';
    var parts = [];
    var owv = item.options_with_values;
    if (owv && owv.length) {
      for (var i = 0; i < owv.length; i++) {
        var name = owv[i] && owv[i].name ? String(owv[i].name) : '';
        var val = owv[i] && owv[i].value ? String(owv[i].value) : '';
        if (!val) continue;
        if (name === 'Title' && (val === 'Default Title' || val === 'Default')) continue;
        if (val === 'Default Title') continue;
        // Show "Name: Value" (e.g. "Color: Gold"); fall back to value only
        // when the option has no usable name.
        parts.push(
          name
            ? '<span class="nucery-cart-drawer__item-variant-name">' + escapeHtml(name) + ':</span> ' + escapeHtml(val)
            : escapeHtml(val)
        );
      }
    }
    if (!parts.length) {
      var vt = item.variant_title || '';
      if (vt && vt !== 'Default Title') parts.push(escapeHtml(vt));
    }
    if (!parts.length) return '';
    return parts.join(' <span class="nucery-cart-drawer__item-variant-sep">/</span> ');
  }

  function createItemElement(item, index, animate) {
    var article = document.createElement('article');
    article.className = 'nucery-cart-drawer__item' + (animate ? ' ncd-item--animate' : '');
    article.setAttribute('data-line-key', item.key);
    article.setAttribute('data-unit-price', item.final_price);
    if (animate) article.style.setProperty('--ncd-item-delay', index * 70 + 'ms');

    var imgUrl = lineItemImageUrl(item);
    var variantLabel = lineItemVariantLabel(item);
    article.innerHTML =
        '<a class="nucery-cart-drawer__item-media" href="' +
        escapeAttr(item.url) +
        '">' +
        (imgUrl
          ? '<img src="' +
            escapeAttr(imgUrl) +
            '" width="88" height="88" loading="lazy" alt="' +
            escapeAttr(item.product_title) +
            '">'
          : '<span class="nucery-cart-drawer__item-ph"></span>') +
        '</a>' +
        '<div class="nucery-cart-drawer__item-body">' +
        '<div class="nucery-cart-drawer__item-top">' +
        '<div class="nucery-cart-drawer__item-titlewrap">' +
        '<a class="nucery-cart-drawer__item-title" href="' +
        escapeAttr(item.url) +
        '">' +
        escapeHtml(item.product_title) +
        '</a>' +
        (variantLabel
          ? '<span class="nucery-cart-drawer__item-variant">' + variantLabel + '</span>'
          : '') +
        '</div>' +
        '<button type="button" class="nucery-cart-drawer__item-remove" data-remove-line="' +
        escapeAttr(item.key) +
        '" aria-label="' +
        escapeAttr(config.strings.remove || 'Remove') +
        '" title="' +
        escapeAttr(config.strings.remove || 'Remove') +
        '">' +
        removeIconInner() +
        '</button></div>' +
        '<div class="nucery-cart-drawer__item-bottom">' +
        '<div class="nucery-cart-drawer__qty" data-line-key="' +
        escapeAttr(item.key) +
        '">' +
        '<button type="button" class="nucery-cart-drawer__qty-btn" data-qty-delta="-1" aria-label="Decrease">−</button>' +
        '<span class="nucery-cart-drawer__qty-val">' +
        item.quantity +
        '</span>' +
        '<button type="button" class="nucery-cart-drawer__qty-btn" data-qty-delta="1" aria-label="Increase">+</button></div>' +
        '<span class="nucery-cart-drawer__item-price">' +
        formatMoney(item.final_line_price) +
        '</span></div></div>';

    return article;
  }

  function updateItemElement(el, item) {
    el.setAttribute('data-unit-price', item.final_price);
    var qtyVal = el.querySelector('.nucery-cart-drawer__qty-val');
    if (qtyVal) qtyVal.textContent = String(item.quantity);
    var price = el.querySelector('.nucery-cart-drawer__item-price');
    if (price) price.innerHTML = formatMoney(item.final_line_price);
  }

  // Render one bundle as a single nested block: the priced anchor on top, the
  // chosen pieces listed beneath as "+ … FREE", a locked "N BUNDLE" pill, and a
  // single remove control that clears the whole group.
  function createBundleGroupElement(name, items, index, animate) {
    var anchor = null, pieces = [];
    items.forEach(function (it) {
      var role = (it.properties && (it.properties._bundle_role || it.properties['_bundle_role'])) || '';
      if (role === 'anchor' && !anchor) anchor = it;
      else pieces.push(it);
    });
    if (!anchor) {
      // Fallback: no explicit anchor — treat the priciest line as the anchor.
      var best = null;
      items.forEach(function (it) { if (!best || Number(it.final_line_price) > Number(best.final_line_price)) best = it; });
      anchor = best || items[0];
      pieces = items.filter(function (it) { return it !== anchor; });
    }

    var keys = items.map(function (it) { return it.key; });
    var article = document.createElement('article');
    article.className = 'nucery-cart-drawer__item nucery-cart-drawer__bundle' + (animate ? ' ncd-item--animate' : '');
    article.setAttribute('data-bundle', name);
    article.setAttribute('data-bundle-keys', keys.join(','));
    if (animate) article.style.setProperty('--ncd-item-delay', index * 70 + 'ms');

    var imgUrl = lineItemImageUrl(anchor);
    var bundleQty = Number(anchor.quantity) || 1;
    var freeWord = (config.strings && config.strings.bundleFree) || 'FREE';
    var bundleWord = (config.strings && config.strings.bundleWord) || 'BUNDLE';

    var childrenHtml = pieces.map(function (it) {
      var vt = (it.variant_title && it.variant_title !== 'Default Title') ? it.variant_title : '';
      var label = escapeHtml(it.product_title || '') + (vt ? ' <span class="ncd-bc-variant">– ' + escapeHtml(vt) + '</span>' : '');
      var q = Number(it.quantity) || 1;
      var qtyTag = q > 1 ? ' <span class="ncd-bc-qty">×' + q + '</span>' : '';
      var price = Number(it.final_line_price) > 0 ? formatMoney(it.final_line_price) : freeWord;
      var cimg = lineItemImageUrl(it);
      var imgTag = cimg
        ? '<img class="ncd-bc-img" src="' + escapeAttr(cimg) + '" width="34" height="34" loading="lazy" alt="">'
        : '<span class="ncd-bc-img ncd-bc-img--ph"></span>';
      return '<li class="ncd-bc-row">' +
        imgTag +
        '<span class="ncd-bc-title">' + label + qtyTag + '</span>' +
        '<span class="ncd-bc-free">' + escapeHtml(price) + '</span></li>';
    }).join('');

    article.innerHTML =
      '<a class="nucery-cart-drawer__item-media" href="' + escapeAttr(anchor.url || '') + '">' +
      (imgUrl
        ? '<img src="' + escapeAttr(imgUrl) + '" width="88" height="88" loading="lazy" alt="' + escapeAttr(anchor.product_title) + '">'
        : '<span class="nucery-cart-drawer__item-ph"></span>') +
      '</a>' +
      '<div class="nucery-cart-drawer__item-body">' +
      '<div class="nucery-cart-drawer__item-top">' +
      '<span class="nucery-cart-drawer__item-title">' + escapeHtml(anchor.product_title) + '</span>' +
      '<button type="button" class="nucery-cart-drawer__item-remove" data-remove-bundle="' + escapeAttr(name) +
      '" aria-label="' + escapeAttr(config.strings.remove || 'Remove') + '" title="' + escapeAttr(config.strings.remove || 'Remove') + '">' +
      removeIconInner() + '</button>' +
      '</div>' +
      (childrenHtml ? '<ul class="nucery-cart-drawer__bundle-children">' + childrenHtml + '</ul>' : '') +
      '<div class="nucery-cart-drawer__item-bottom">' +
      '<div class="nucery-cart-drawer__qty nucery-cart-drawer__qty--locked nucery-cart-drawer__bundle-pill">' +
      '<span class="nucery-cart-drawer__qty-val">' + bundleQty + '</span>' +
      '<span class="nucery-cart-drawer__bundle-pill-lbl">' + escapeHtml(bundleWord) + '</span></div>' +
      '<span class="nucery-cart-drawer__item-price">' + formatMoney(anchor.final_line_price) + '</span>' +
      '</div></div>';

    return article;
  }

  function renderItems(cart, options) {
    options = options || {};
    var animate = !!options.animate;
    var host = qs('[data-nucery-cart-items]');
    if (!host) return;

    if (!cart.items || !cart.items.length) {
      host.innerHTML = '';
      return;
    }

    // Group lines sharing a `_bundle` property (added by the bundle builder) so a
    // bundle renders as one nested block; everything else renders as usual.
    // A full rebuild keeps the grouping logic simple and correct.
    var units = [];
    var bundleIndex = {};
    cart.items.forEach(function (item) {
      var props = item.properties || {};
      var bundleName = props._bundle || props['_bundle'] || '';
      if (bundleName) {
        var u = bundleIndex[bundleName];
        if (!u) { u = { type: 'bundle', name: bundleName, items: [] }; bundleIndex[bundleName] = u; units.push(u); }
        u.items.push(item);
      } else {
        units.push({ type: 'item', item: item });
      }
    });

    host.innerHTML = '';
    units.forEach(function (u, i) {
      if (u.type === 'bundle') {
        host.appendChild(createBundleGroupElement(u.name, u.items, i, animate));
      } else {
        host.appendChild(createItemElement(u.item, i, animate));
      }
    });
  }

  function escapeHtml(s) {
    var d = document.createElement('div');
    d.textContent = s == null ? '' : String(s);
    return d.innerHTML;
  }

  function escapeAttr(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  /* ===========================================================================
     Discount / coupon support
     - Apply: GET /discount/CODE (sets Shopify discount_code cookie) → re-read cart
     - Reflect: /cart.js returns discount_codes[] + total_discount for
       cart/product-eligible discounts (shipping-only discounts show at checkout)
     - Remove: expire the discount_code cookie → re-read cart
     ========================================================================= */
  var DISCOUNT_TAG_ICON =
    '<svg class="nucery-cart-drawer__discount-tag-ic" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<path d="M20.59 13.41 13.42 20.6a2 2 0 0 1-2.83 0L3 13V3h10l7.59 7.59a2 2 0 0 1 0 2.82Z"></path><circle cx="7.5" cy="7.5" r="1.3"></circle></svg>';

  function getCookie(name) {
    var m = document.cookie.match('(?:^|; )' + name.replace(/([.$?*|{}()\[\]\\\/+^])/g, '\\$1') + '=([^;]*)');
    return m ? decodeURIComponent(m[1]) : '';
  }

  function cartAppliedCodes(cart) {
    if (!cart || !Array.isArray(cart.discount_codes)) return [];
    return cart.discount_codes.filter(function (d) {
      return d && d.applicable && d.code;
    });
  }

  function cartHasCode(cart, code) {
    if (!code) return false;
    var c = String(code).toUpperCase();
    return cartAppliedCodes(cart).some(function (d) {
      return String(d.code).toUpperCase() === c;
    });
  }

  /** Codes currently shown as applied chips (read from the DOM, no fetch). */
  function renderedDiscountCodes() {
    if (!root) return [];
    return qsa('[data-remove-discount]')
      .map(function (b) {
        return b.getAttribute('data-remove-discount');
      })
      .filter(Boolean);
  }

  function setDiscountBusy(busy) {
    var block = qs('[data-nucery-discount]');
    if (block) block.classList.toggle('nucery-cart-drawer__discount--busy', !!busy);
    var btn = qs('[data-nucery-discount-apply]');
    if (btn) btn.disabled = !!busy;
  }

  /** Visual state of the discount field: '' (neutral) | 'error' (red) | 'success' (green). */
  function setDiscountState(state) {
    var block = qs('[data-nucery-discount]');
    if (!block) return;
    block.classList.toggle('nucery-cart-drawer__discount--error', state === 'error');
    block.classList.toggle('nucery-cart-drawer__discount--success', state === 'success');
  }

  function showDiscountError(msg) {
    var el = qs('[data-nucery-discount-error]');
    if (msg) {
      if (el) {
        el.textContent = msg;
        el.hidden = false;
      }
      setDiscountState('error');
    } else {
      if (el) {
        el.textContent = '';
        el.hidden = true;
      }
      // Only drop the error state here; success is cleared explicitly.
      var block = qs('[data-nucery-discount]');
      if (block) block.classList.remove('nucery-cart-drawer__discount--error');
    }
  }

  function updateCartDiscount(discountValue) {
    // Official Cart AJAX API: POST /cart/update.js {discount} adds/replaces all
    // codes; an empty string clears them. Returns the updated cart.
    return fetch(cartUrl('cart/update.js'), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: discountValue }),
    }).then(function (r) {
      return r.json();
    });
  }

  function applyDiscount(code) {
    code = String(code == null ? '' : code).trim();
    if (!code || !ensureRoot()) return Promise.resolve();
    readConfig();
    // Fixed-price bundles can't be combined with discount codes.
    if (qsa('[data-bundle-keys]').length) {
      showDiscountError(config.strings.discountBundleError || 'Discount codes can’t be used with a bundle.');
      return Promise.resolve();
    }
    setDiscountBusy(true);
    showDiscountError('');
    // Send already-applied codes + the new one (deduped). Shopify's `discount`
    // param replaces ALL codes, so without this an invalid new code would wipe
    // a valid one already on the cart. Including the existing codes keeps them.
    var seen = {};
    var union = [];
    renderedDiscountCodes()
      .concat([code])
      .forEach(function (c) {
        var k = String(c).toUpperCase();
        if (!seen[k]) {
          seen[k] = 1;
          union.push(c);
        }
      });
    return updateCartDiscount(union.join(','))
      .then(function (cart) {
        applyCart(cart, { animate: false });
        if (cartHasCode(cart, code)) {
          var input = qs('[data-nucery-discount-input]');
          if (input) input.value = '';
          showDiscountError('');
          setDiscountState('success');
        } else {
          // Endpoint accepted the request but the code is invalid or not
          // applicable to the current cart (discount_codes → applicable:false).
          showDiscountError(config.strings.discountError || 'This code isn’t valid for your cart.');
        }
        return cart;
      })
      .catch(function () {
        showDiscountError(config.strings.discountError || 'Something went wrong. Please try again.');
      })
      .finally(function () {
        setDiscountBusy(false);
      });
  }

  function removeDiscount() {
    if (!ensureRoot()) return Promise.resolve();
    setDiscountBusy(true);
    return updateCartDiscount('')
      .then(function (cart) {
        applyCart(cart, { animate: false });
        setDiscountState('');
        return cart;
      })
      .finally(function () {
        setDiscountBusy(false);
      });
  }

  function renderDiscounts(cart) {
    var tags = qs('[data-nucery-discount-tags]');
    if (!tags) return;
    var codes = cartAppliedCodes(cart);
    if (!codes.length) {
      tags.innerHTML = '';
      tags.hidden = true;
      return;
    }
    tags.innerHTML = codes
      .map(function (d) {
        return (
          '<li class="nucery-cart-drawer__discount-tag">' +
          DISCOUNT_TAG_ICON +
          '<span class="nucery-cart-drawer__discount-tag-code">' +
          escapeHtml(d.code) +
          '</span>' +
          '<button type="button" class="nucery-cart-drawer__discount-remove" data-remove-discount="' +
          escapeAttr(d.code) +
          '" aria-label="' +
          escapeAttr((config.strings.remove || 'Remove') + ' ' + d.code) +
          '"><svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M6 6 18 18M18 6 6 18"></path></svg></button>' +
          '</li>'
        );
      })
      .join('');
    tags.hidden = false;
    showDiscountError('');
  }

  /** Original (struck) subtotal + "you save" line, driven by cart.total_discount. */
  function renderSavings(cart) {
    var savings = Number(cart.total_discount) || 0;
    var row = qs('[data-nucery-savings]');
    var amt = qs('[data-nucery-savings-amount]');
    var orig = qs('[data-nucery-subtotal-original]');
    if (amt) amt.innerHTML = '−' + formatMoney(savings);
    if (row) row.hidden = !(savings > 0);
    if (orig) {
      if (savings > 0) {
        // Plain text (no .money span) so the Vitals currency converter doesn't
        // attach a base-currency hover tooltip to this struck reference price.
        orig.textContent = formatMoneyPlain(cartTotalCents(cart) + savings);
        orig.hidden = false;
      } else {
        orig.textContent = '';
        orig.hidden = true;
      }
    }
  }

  function parseAmountToCents(raw) {
    if (raw == null) return null;
    var s = String(raw).trim().replace(/\s/g, '');
    if (s === '') return null;
    s = s.replace(',', '.');
    s = s.replace(/[^\d.-]/g, '');
    var n = parseFloat(s);
    if (!isFinite(n) || n < 0) return null;
    return Math.round(n * 100);
  }

  /**
   * Theme thresholds are entered in the store’s primary currency (minor units).
   * Cart /cart.js totals use the customer’s presentment currency when Markets is on.
   * Shopify injects window.Shopify.currency.rate (shop → active).
   */
  function convertShopMinorToCartMinor(shopMinor) {
    var n = Number(shopMinor);
    if (!isFinite(n)) return 0;
    var shop = (config.shopCurrency || '').toString().toUpperCase();
    var cartCur = (config.currency || '').toString().toUpperCase();
    if (!shop || !cartCur || shop === cartCur) return Math.round(n);
    var sc = window.Shopify && window.Shopify.currency;
    var rate = sc && sc.rate != null ? Number(sc.rate) : NaN;
    if (!isFinite(rate) || rate <= 0) return Math.round(n);
    return Math.round(n * rate);
  }

  function applyThresholdsFromConfig() {
    var s1 = parseAmountToCents(config.shippingAmountRaw);
    var s2 = parseAmountToCents(config.giftAmountRaw);
    var fb1 = Number(config.shippingThresholdCentsFallback);
    var fb2 = Number(config.giftThresholdCentsFallback);
    if (!isFinite(fb1)) fb1 = 3500;
    if (!isFinite(fb2)) fb2 = 7000;
    var base1 = s1 != null ? s1 : fb1;
    var base2 = s2 != null ? s2 : fb2;
    config.shippingThresholdCents = convertShopMinorToCartMinor(base1);
    config.giftThresholdCents = convertShopMinorToCartMinor(base2);
    if (config.giftThresholdCents <= config.shippingThresholdCents) {
      config.giftThresholdCents = config.shippingThresholdCents + 1;
    }
  }

  /** Free shipping milestone is fixed at 50% of the bar; fill maps [0,m1)→[0,50%), [m1,m2)→[50%,100%). */
  var SHIP_VISUAL_PCT = 50;

  function progressState(totalCents) {
    var m1 = Number(config.shippingThresholdCents);
    if (!isFinite(m1)) m1 = 3500;
    m1 = Math.max(0, m1);
    var m2 = Number(config.giftThresholdCents);
    if (!isFinite(m2)) m2 = 7000;
    m2 = Math.max(0, m2);
    if (m2 <= m1) m2 = m1 + 1;
    var t = totalCents || 0;
    var remainingShip = Math.max(0, m1 - t);
    var remainingGift = Math.max(0, m2 - t);
    var shipDone = m1 === 0 ? true : t >= m1;
    var giftDone = t >= m2;
    var fillPct;
    if (t >= m2) {
      fillPct = 100;
    } else if (m1 <= 0) {
      fillPct = (t / m2) * 100;
    } else if (t < m1) {
      fillPct = (t / m1) * SHIP_VISUAL_PCT;
    } else {
      var seg = (t - m1) / (m2 - m1);
      fillPct = SHIP_VISUAL_PCT + seg * (100 - SHIP_VISUAL_PCT);
    }
    fillPct = Math.min(100, Math.max(0, fillPct));
    return {
      m1: m1,
      m2: m2,
      t: t,
      remainingShip: remainingShip,
      remainingGift: remainingGift,
      shipDone: shipDone,
      giftDone: giftDone,
      fillPct: fillPct,
      shipDotPct: SHIP_VISUAL_PCT,
    };
  }

  function renderProgress(cart) {
    if (!isProgressEnabled()) {
      clearProgressState();
      return;
    }
    var wrap = qs('[data-nucery-cart-progress]');
    if (!wrap) return;
    var total = cartTotalCents(cart);
    var st = progressState(total);
    var msgEl = qs('[data-nucery-progress-msg]');
    var barFill = qs('[data-nucery-progress-fill]');

    if (msgEl) {
      if (st.giftDone) {
        msgEl.textContent = config.strings.progressAllMet || '';
      } else if (st.shipDone) {
        var tplGift = config.strings.progressGiftRemaining || '';
        msgEl.textContent = tplGift.replace(/__AMOUNT__/g, formatMoneyPlain(st.remainingGift));
      } else {
        var tpl = config.strings.progressShippingRemaining || '';
        msgEl.textContent = tpl.replace(/__AMOUNT__/g, formatMoneyPlain(st.remainingShip));
      }
    }
    if (barFill) barFill.style.width = st.fillPct + '%';

    if (root) {
      var prev = prevTierState;
      root.classList.toggle('ncd--ship-reached', st.shipDone);
      root.classList.toggle('ncd--gift-reached', st.giftDone);
      if (prev && !prev.shipDone && st.shipDone) {
        root.classList.add('ncd--just-ship');
        clearTimeout(root._ncdShipPopT);
        root._ncdShipPopT = setTimeout(function () {
          root.classList.remove('ncd--just-ship');
        }, 700);
      }
      if (prev && !prev.giftDone && st.giftDone) {
        root.classList.add('ncd--just-gift');
        clearTimeout(root._ncdGiftPopT);
        root._ncdGiftPopT = setTimeout(function () {
          root.classList.remove('ncd--just-gift');
        }, 700);
      }
      prevTierState = { shipDone: st.shipDone, giftDone: st.giftDone };
    }

    var startEl = qs('[data-nucery-track-start]');
    if (startEl) {
      var ov = config.trackStartOverride && String(config.trackStartOverride).trim();
      if (ov) startEl.textContent = ov;
      else startEl.innerHTML = formatMoney(0);
    }

    var m1Amt = qs('[data-nucery-milestone-ship-amt]');
    var m2Amt = qs('[data-nucery-milestone-gift-amt]');
    if (m1Amt) m1Amt.innerHTML = formatMoney(st.m1);
    if (m2Amt) m2Amt.innerHTML = formatMoney(st.m2);
  }

  function renderTotals(cart) {
    var sub = qs('[data-nucery-subtotal]');
    var chk = qs('[data-nucery-checkout-price]');
    var titleCount = qs('[data-nucery-cart-title-count]');
    var total = cartTotalCents(cart);
    var count = cart.item_count || 0;
    if (sub) sub.innerHTML = formatMoney(total);
    if (chk) chk.innerHTML = formatMoney(total);
    if (titleCount) titleCount.textContent = String(count);
    updateHeaderBadges(count);
  }

  function toggleEmpty(cart) {
    var empty = !cart.items || !cart.items.length;
    var emptyEl = qs('[data-nucery-cart-empty]');
    var filledEl = qs('[data-nucery-cart-filled]');
    if (emptyEl) emptyEl.hidden = !empty;
    if (filledEl) filledEl.hidden = empty;
    var footLines = qs('[data-nucery-cart-footer-lines]');
    var payRow = qs('[data-nucery-payment-row]');
    if (footLines) footLines.hidden = empty;
    if (payRow) payRow.hidden = empty;
    var scrollEl = qs('.nucery-cart-drawer__scroll');
    if (scrollEl) scrollEl.classList.toggle('nucery-cart-drawer__scroll--empty', empty);
  }

  function cartHasBundle(cart) {
    return !!(cart && Array.isArray(cart.items) && cart.items.some(function (it) {
      return it.properties && (it.properties._bundle || it.properties['_bundle']);
    }));
  }

  function applyCart(cart, options) {
    if (!ensureRoot()) return cart;
    options = options || {};
    if (cart.currency) config.currency = cart.currency;
    applyThresholdsFromConfig();
    toggleEmpty(cart);
    renderItems(cart, options);
    renderProgress(cart);
    renderTotals(cart);
    renderDiscounts(cart);
    renderSavings(cart);
    // A fixed-price bundle can't be combined with discount codes — if one is on
    // the cart while a bundle is present, strip it and re-render clean.
    if (cartHasBundle(cart) && cartAppliedCodes(cart).length && !applyCart._stripping) {
      applyCart._stripping = true;
      updateCartDiscount('')
        .then(function (c) {
          applyCart._stripping = false;
          if (c && Array.isArray(c.items)) applyCart(c, { animate: false });
        })
        .catch(function () { applyCart._stripping = false; });
    }
    return cart;
  }

  function refresh(options) {
    if (!ensureRoot()) return Promise.resolve();
    readConfig();
    return fetchCart().then(function (cart) {
      return applyCart(cart, options);
    });
  }

  function openDrawer() {
    if (!ensureRoot()) return Promise.resolve();
    readConfig();
    suppressLegacyCartUi();
    var wasOpen = open;
    if (!open) {
      open = true;
      root.classList.remove('nucery-cart-drawer--is-closing');
      root.classList.add('nucery-cart-drawer--is-open');
      root.setAttribute('aria-hidden', 'false');
      setBodyScroll(true);
    }
    return refresh({ animate: !wasOpen }).then(function (cart) {
      if (!wasOpen) {
        var closeBtn = qs('[data-nucery-cart-close]');
        if (closeBtn) closeBtn.focus();
      }
      return cart;
    });
  }

  /** After add-to-cart: load /cart.js first, then reveal drawer with the new line item. */
  function openDrawerWithFreshCart() {
    if (!ensureRoot()) return Promise.resolve();
    readConfig();
    suppressLegacyCartUi();
    if (open) {
      return refresh({ animate: true });
    }
    return fetchCart().then(function (cart) {
      applyCart(cart, { animate: true });
      open = true;
      root.classList.remove('nucery-cart-drawer--is-closing');
      root.classList.add('nucery-cart-drawer--is-open');
      root.setAttribute('aria-hidden', 'false');
      setBodyScroll(true);
      var closeBtn = qs('[data-nucery-cart-close]');
      if (closeBtn) closeBtn.focus();
      return cart;
    });
  }

  var CART_CLOSE_MS = 420;

  function finishCartClose() {
    if (!root) return;
    root.classList.remove('nucery-cart-drawer--is-closing');
  }

  function closeDrawer() {
    if (!open) return;
    open = false;
    ensureRoot();
    if (!root) return;
    root.classList.add('nucery-cart-drawer--is-closing');
    root.classList.remove('nucery-cart-drawer--is-open');
    root.setAttribute('aria-hidden', 'true');
    setBodyScroll(false);

    var panel = qs('.nucery-cart-drawer__panel');
    var done = false;
    function end() {
      if (done) return;
      done = true;
      if (panel) panel.removeEventListener('transitionend', onEnd);
      finishCartClose();
    }
    function onEnd(e) {
      if (!panel || e.target !== panel || e.propertyName !== 'transform') return;
      end();
    }
    if (panel) panel.addEventListener('transitionend', onEnd);
    window.setTimeout(end, CART_CLOSE_MS + 60);
  }

  function bindOpens() {
    document.addEventListener(
      'click',
      function (e) {
        var t = e.target.closest('[data-nucery-cart-open]');
        if (!t) return;
        e.preventDefault();
        e.stopPropagation();
        suppressLegacyCartUi();
        openDrawer();
      },
      true
    );
  }

  var drawerDocListenersBound = false;

  function bindRoot() {
    if (drawerDocListenersBound) return;
    drawerDocListenersBound = true;

    document.addEventListener(
      'click',
      function (e) {
        if (!e.target.closest('#NuceryCartDrawer')) return;
        if (e.target.closest('[data-nucery-cart-close]') || e.target.closest('[data-nucery-cart-backdrop]')) {
          closeDrawer();
          return;
        }
        var rmBundle = e.target.closest('[data-remove-bundle]');
        if (rmBundle) {
          e.preventDefault();
          removeBundle(rmBundle.getAttribute('data-remove-bundle'));
          return;
        }
        var rm = e.target.closest('[data-remove-line]');
        if (rm) {
          e.preventDefault();
          changeLine(rm.getAttribute('data-remove-line'), 0);
          return;
        }
        var rmDisc = e.target.closest('[data-remove-discount]');
        if (rmDisc) {
          e.preventDefault();
          removeDiscount(rmDisc.getAttribute('data-remove-discount'));
          return;
        }
        var deltaBtn = e.target.closest('[data-qty-delta]');
        if (deltaBtn) {
          e.preventDefault();
          var row = deltaBtn.closest('.nucery-cart-drawer__item');
          if (!row) return;
          var key = row.getAttribute('data-line-key');
          var delta = parseInt(deltaBtn.getAttribute('data-qty-delta'), 10);
          var valEl = row.querySelector('.nucery-cart-drawer__qty-val');
          var cur = parseInt(valEl && valEl.textContent, 10) || 1;
          var next = Math.max(0, cur + delta);
          if (valEl) valEl.textContent = String(next);
          // Optimistic line price now; server reconciles subtotal/discounts on
          // the debounced /cart/change.js call.
          var unit = parseInt(row.getAttribute('data-unit-price'), 10);
          var priceEl = row.querySelector('.nucery-cart-drawer__item-price');
          if (priceEl && isFinite(unit)) priceEl.innerHTML = formatMoney(unit * next);
          if (next === 0) row.classList.add('nucery-cart-drawer__item--busy');
          queueLineChange(key, next);
        }
      },
      false
    );

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && open) closeDrawer();
    });

    document.addEventListener(
      'submit',
      function (e) {
        var form = e.target;
        if (!form || !form.matches || !form.matches('[data-nucery-discount-form]')) return;
        e.preventDefault();
        var input = form.querySelector('[data-nucery-discount-input]');
        applyDiscount(input ? input.value : '');
      },
      true
    );

    // Reset to neutral (clear red error + green success) as soon as the shopper
    // edits the discount field.
    document.addEventListener('input', function (e) {
      if (e.target && e.target.matches && e.target.matches('[data-nucery-discount-input]')) {
        showDiscountError('');
        setDiscountState('');
      }
    });
  }

  /** Route all theme / Alpine cart entry points to this drawer. */
  function installCartTakeover() {
    function ncdOpen() {
      if (window.NuceryCartDrawer) window.NuceryCartDrawer.open();
    }
    function ncdClose() {
      if (window.NuceryCartDrawer) window.NuceryCartDrawer.close();
    }
    function ncdUpdate(shouldOpen) {
      if (shouldOpen) ncdOpen();
      var p = shouldOpen
        ? Promise.resolve()
        : window.NuceryCartDrawer
          ? window.NuceryCartDrawer.refresh()
          : Promise.resolve();
      return p.then(function () {
        suppressLegacyCartUi();
      });
    }

    window.openCart = ncdOpen;
    window.closeCart = ncdClose;
    window.updateCart = ncdUpdate;
    window.__ncdCartTakeover = { open: ncdOpen, close: ncdClose, update: ncdUpdate };

    patchImpulseProductFormSuccess();
    neutralizeImpulseCartDrawer();
    suppressLegacyCartUi();
  }

  /** Prevent Impulse `cartType: page` redirect when Nucery drawer is active. */
  function patchImpulseProductFormSuccess() {
    if (!window.theme || !window.theme.AjaxProduct) return;
    var proto = window.theme.AjaxProduct.prototype;
    if (!proto || proto.__ncdSuccessPatched) return;
    var originalSuccess = proto.success;
    proto.success = function (product) {
      var settings = window.theme && window.theme.settings;
      var prevCartType = settings && settings.cartType;
      if (prevCartType === 'page' && window.NuceryCartDrawer && settings) {
        settings.cartType = 'drawer';
      }
      try {
        originalSuccess.call(this, product);
      } finally {
        if (settings && prevCartType === 'page' && window.NuceryCartDrawer) {
          settings.cartType = prevCartType;
        }
      }
    };
    proto.__ncdSuccessPatched = true;
  }

  function neutralizeImpulseCartDrawer() {
    if (!window.theme || !window.theme.CartDrawer) return;
    var proto = window.theme.CartDrawer.prototype;
    if (proto && !proto.__ncdPatched) {
      proto.open = function () {
        if (window.NuceryCartDrawer) window.NuceryCartDrawer.open();
      };
      proto.close = function () {
        if (window.NuceryCartDrawer) window.NuceryCartDrawer.close();
      };
      proto.__ncdPatched = true;
    }
  }

  /** Hide upsell slide-out + mini alert cart (theme__cart in overlay-group). */
  function suppressLegacyCartUi() {
    document.documentElement.classList.remove('js-drawer-open', 'js-drawer-open--cart', 'js-drawer-closing');

    var impulseDrawer = document.getElementById('CartDrawer');
    if (impulseDrawer) {
      impulseDrawer.setAttribute('aria-hidden', 'true');
      impulseDrawer.classList.remove('drawer--is-open');
    }

    var legacySections = document.querySelectorAll(
      '.shopify-section[id*="theme__cart"], .shopify-section[id*="theme_cart"], [data-ncd-legacy-cart]'
    );
    legacySections.forEach(function (el) {
      el.setAttribute('data-ncd-legacy-cart', '');
      el.setAttribute('aria-hidden', 'true');
    });

    try {
      var body = document.body;
      if (body && body._x_dataStack && body._x_dataStack.length) {
        body._x_dataStack.forEach(function (data) {
          if (data && Object.prototype.hasOwnProperty.call(data, 'cart_alert')) {
            data.cart_alert = false;
          }
        });
      }
    } catch (err) {
      /* Alpine optional */
    }
  }

  function dispatchAjaxProductAdded(data, triggerEl) {
    if (data && data.status === 422) {
      document.dispatchEvent(new CustomEvent('ajaxProduct:added'));
      return;
    }
    document.dispatchEvent(
      new CustomEvent('ajaxProduct:added', {
        bubbles: true,
        detail: { product: data, addToCartBtn: triggerEl || null },
      })
    );
  }

  function ajaxAddVariant(variantId, quantity, triggerEl) {
    var qty = quantity || 1;
    var formData = new FormData();
    formData.append('id', String(variantId));
    formData.append('quantity', String(qty));
    var addUrl = cartUrl('cart/add.js');
    if (window.theme && window.theme.routes && window.theme.routes.cartAdd) {
      addUrl = window.theme.routes.cartAdd;
    }
    if (triggerEl && triggerEl.setAttribute) {
      triggerEl.setAttribute('aria-busy', 'true');
    }
    return fetch(addUrl, {
      method: 'POST',
      body: formData,
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin',
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (data) {
        dispatchAjaxProductAdded(data, triggerEl);
        return data;
      })
      .finally(function () {
        if (triggerEl && triggerEl.removeAttribute) {
          triggerEl.removeAttribute('aria-busy');
        }
      });
  }

  function isInterceptedCartAddForm(form) {
    if (!form || !form.matches) return false;
    if (form.matches('form.bestsellers-card__quick')) return true;
    if (form.querySelector('[data-cfs-add]')) return true;
    return false;
  }

  /**
   * Sections that POST to /cart/add (bestsellers cards, custom follow look items, etc.)
   * are converted to AJAX add + Nucery drawer open.
   */
  function bindInterceptedCartAddForms() {
    if (bindInterceptedCartAddForms._bound) return;
    bindInterceptedCartAddForms._bound = true;

    document.addEventListener(
      'submit',
      function (e) {
        var form = e.target;
        if (!isInterceptedCartAddForm(form)) return;
        e.preventDefault();
        e.stopPropagation();
        var idInput = form.querySelector('input[name="id"]');
        var qtyInput = form.querySelector('input[name="quantity"]');
        var variantId = idInput ? parseInt(idInput.value, 10) : 0;
        if (!variantId) return;
        var qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
        var btn = form.querySelector('[data-bst-quick], [data-cfs-add]');
        ajaxAddVariant(variantId, qty, btn);
      },
      true
    );
  }

  function onCartOpenRequest(e) {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
    if (e && typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
    suppressLegacyCartUi();
    if (!window.NuceryCartDrawer) return;
    if (e && e.type === 'ajaxProduct:added') {
      var added = e.detail && e.detail.product;
      if (added && added.status === 422) return;
      openDrawerWithFreshCart();
      return;
    }
    if (open) window.NuceryCartDrawer.refresh();
    else window.NuceryCartDrawer.open();
  }

  function bindCartTakeoverEvents() {
    if (bindCartTakeoverEvents._bound) return;
    bindCartTakeoverEvents._bound = true;

    document.addEventListener('ajaxProduct:added', onCartOpenRequest, true);
    document.addEventListener('cart:open', onCartOpenRequest, true);

    document.addEventListener(
      'cart:updated',
      function () {
        suppressLegacyCartUi();
      },
      false
    );
  }

  function onSectionLoad(e) {
    var sec = e && e.target;
    if (!sec || typeof sec.querySelector !== 'function') return;
    if (!sec.querySelector('#NuceryCartDrawer')) return;
    prevTierState = null;
    if (open) {
      open = false;
      setBodyScroll(false);
    }
    ensureRoot();
    readConfig();
    refresh();
  }

  /**
   * Arriving via a /discount/CODE?redirect=… link: Shopify has already set the
   * discount_code cookie + applied it to the cart. Pop the drawer once per
   * session per code so the shopper sees the reduced price.
   */
  function getQueryParam(name) {
    try {
      return new URLSearchParams(window.location.search).get(name);
    } catch (e) {
      return null;
    }
  }

  /** Drop a query param from the visible URL so a refresh won't re-trigger it. */
  function stripQueryParam(name) {
    try {
      var url = new URL(window.location.href);
      if (!url.searchParams.has(name)) return;
      url.searchParams.delete(name);
      var qs = url.searchParams.toString();
      window.history.replaceState({}, '', url.pathname + (qs ? '?' + qs : '') + url.hash);
    } catch (e) {}
  }

  /**
   * True when this page was reached via a server redirect (redirectCount > 0).
   * Visiting /discount/CODE issues a 302 to the redirect target, so a bare
   * /discount/70OFFNOW link lands here with redirectCount >= 1 — that's our
   * marker-free signal that the shopper just used a discount link.
   */
  function navWasRedirected() {
    try {
      var entries = performance.getEntriesByType && performance.getEntriesByType('navigation');
      if (entries && entries[0] && typeof entries[0].redirectCount === 'number') {
        return entries[0].redirectCount > 0;
      }
      if (performance.navigation && typeof performance.navigation.redirectCount === 'number') {
        return performance.navigation.redirectCount > 0;
      }
    } catch (e) {}
    return false;
  }

  function maybeAutoOpenForDiscount() {
    if (maybeAutoOpenForDiscount._done) return;
    maybeAutoOpenForDiscount._done = true;
    if (!ensureRoot()) return;

    // Force-open marker: discount links should redirect to a URL carrying
    // ?ncd_cart=1 (or #opencart). This opens the drawer EVERY time the link is
    // used — even if the code is already applied — then the marker is removed.
    var forceOpen =
      getQueryParam('ncd_cart') === '1' ||
      getQueryParam('open_cart') === '1' ||
      /(?:^|#)opencart$/.test(window.location.hash);
    if (forceOpen) {
      stripQueryParam('ncd_cart');
      stripQueryParam('open_cart');
      if (/opencart/.test(window.location.hash)) {
        try {
          window.history.replaceState({}, '', window.location.pathname + window.location.search);
        } catch (e) {}
      }
      openDrawer();
      return;
    }

    // Otherwise: only auto-open for a VALID, applicable discount CODE on the
    // cart (not an automatic/leftover discount, and not an invalid code).
    var viaRedirect = navWasRedirected();
    var attempted = getCookie('discount_code'); // code Shopify just tried (may be '')
    fetchCart().then(function (cart) {
      if ((cart.item_count || 0) <= 0) return;

      var codes = cartAppliedCodes(cart); // applicable codes only
      if (!codes.length) return; // invalid/expired code → nothing valid → don't open

      // If we know the code that was just attempted (cookie), require THAT code
      // to be the valid one — so an invalid link doesn't pop the drawer just
      // because an older valid code is still on the cart.
      if (attempted && !cartHasCode(cart, attempted)) return;

      // Reached via a redirect (e.g. a bare /discount/CODE link) → open EVERY
      // time, no session gate. Normal page visits open once per session per code.
      if (!viaRedirect) {
        var key = codes
          .map(function (d) {
            return String(d.code).toUpperCase();
          })
          .join('|');
        var seenKey = 'ncd_disc_open_' + key;
        try {
          if (sessionStorage.getItem(seenKey)) return;
          sessionStorage.setItem(seenKey, '1');
        } catch (e) {}
      }
      openDrawer();
    });
  }

  function init() {
    bindInterceptedCartAddForms();
    if (!ensureRoot()) return;
    readConfig();

    installCartTakeover();
    bindCartTakeoverEvents();
    bindOpens();
    bindRoot();

    document.addEventListener('shopify:section:load', onSectionLoad);
    document.addEventListener('shopify:section:select', onSectionLoad);

    document.addEventListener('cart:updated', function () {
      refresh();
    });

    refresh();
    maybeAutoOpenForDiscount();

    window.addEventListener('load', function () {
      installCartTakeover();
      patchImpulseProductFormSuccess();
      neutralizeImpulseCartDrawer();
      suppressLegacyCartUi();
    });

    var takeoverAttempts = 0;
    var takeoverTimer = setInterval(function () {
      installCartTakeover();
      patchImpulseProductFormSuccess();
      neutralizeImpulseCartDrawer();
      if (++takeoverAttempts >= 20) clearInterval(takeoverTimer);
    }, 250);
  }

  window.NuceryCartDrawer = {
    open: openDrawer,
    openAfterAdd: openDrawerWithFreshCart,
    close: closeDrawer,
    refresh: refresh,
    isOpen: function () {
      return open;
    },
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
